import { useEffect, useRef, useState, type ReactNode } from 'react';
import {
  strainInputSchema,
  type Strain,
  type StrainInput,
  type SourceType,
} from '@shared';
import {
  ACTIVE_PRODUCT_CATEGORIES,
  ACTIVE_STRAIN_CLASSIFICATIONS,
  applyOptionalFieldDraft,
  buildStrainFormCandidate,
  clearOptionalField,
  createOptionalFieldDraft,
  hideOptionalField,
  initialVisibleOptionalFields,
  isActiveProductCategory,
  isActiveStrainClassification,
  OPTIONAL_PRODUCT_FIELDS,
  toStrainFormState,
  validateOptionalFieldDraft,
  type OptionalProductFieldKey,
  type OptionalProductFieldDraft,
  type StrainFormState,
} from './strainFormModel';

export { deriveProductDisplayName } from './strainFormModel';

/**
 * Manual product entry/edit form. Supports optional cannabinoids and notes.
 * Does not invent spreadsheet Product IDs.
 */

export interface StrainFormProps {
  initial?: Partial<Strain>;
  submitLabel?: string;
  onSubmit: (input: StrainInput) => Promise<void> | void;
  busy?: boolean;
  /** Forced source for create (default local_manual). */
  forceSourceType?: SourceType;
  /** Create-only content placed after Lineage and before the submit action. */
  afterLineage?: ReactNode;
}

export function StrainForm({
  initial,
  submitLabel = 'Save product',
  onSubmit,
  busy = false,
  forceSourceType = 'local_manual',
  afterLineage,
}: StrainFormProps): JSX.Element {
  const [state, setState] = useState<StrainFormState>(() =>
    toStrainFormState(initial),
  );
  const [visibleOptionalFields, setVisibleOptionalFields] = useState(() =>
    initialVisibleOptionalFields(initial),
  );
  const [optionalModalOpen, setOptionalModalOpen] = useState(false);
  const [optionalDraft, setOptionalDraft] = useState<OptionalProductFieldDraft>(
    () => createOptionalFieldDraft(toStrainFormState(initial)),
  );
  const [optionalDraftErrors, setOptionalDraftErrors] = useState<
    Partial<Record<OptionalProductFieldKey, string>>
  >({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [formError, setFormError] = useState<string | null>(null);
  const addOptionalButtonRef = useRef<HTMLButtonElement>(null);
  const firstOptionalInputRef = useRef<HTMLInputElement>(null);

  const set = <K extends keyof StrainFormState>(
    key: K,
    value: StrainFormState[K],
  ): void => {
    setState((s) => ({ ...s, [key]: value }));
  };

  const openOptionalModal = (): void => {
    setOptionalDraft(createOptionalFieldDraft(state));
    setOptionalDraftErrors({});
    setOptionalModalOpen(true);
  };

  const closeOptionalModal = (): void => {
    setOptionalModalOpen(false);
    setOptionalDraftErrors({});
    requestAnimationFrame(() => addOptionalButtonRef.current?.focus());
  };

  const confirmOptionalDraft = (): void => {
    const draftErrors = validateOptionalFieldDraft(optionalDraft);
    if (Object.keys(draftErrors).length > 0) {
      setOptionalDraftErrors(draftErrors);
      return;
    }
    const applied = applyOptionalFieldDraft(
      state,
      visibleOptionalFields,
      optionalDraft,
    );
    setState(applied.state);
    setVisibleOptionalFields(applied.visible);
    setOptionalModalOpen(false);
    setOptionalDraftErrors({});
    requestAnimationFrame(() => addOptionalButtonRef.current?.focus());
  };

  const removeOptionalField = (key: OptionalProductFieldKey): void => {
    setState((current) => clearOptionalField(current, key));
    setVisibleOptionalFields((current) => hideOptionalField(current, key));
  };

  useEffect(() => {
    if (!optionalModalOpen) return;
    const focusFrame = requestAnimationFrame(() =>
      firstOptionalInputRef.current?.focus(),
    );
    const closeFromEscape = (event: KeyboardEvent): void => {
      if (event.key === 'Escape') closeOptionalModal();
    };
    document.addEventListener('keydown', closeFromEscape);
    return () => {
      cancelAnimationFrame(focusFrame);
      document.removeEventListener('keydown', closeFromEscape);
    };
  }, [optionalModalOpen]);

  const currentLegacyCategory =
    state.category && !isActiveProductCategory(state.category)
      ? state.category
      : null;
  const currentLegacyStrainClassification =
    state.strainClassSourceText &&
    !isActiveStrainClassification(state.strainClassSourceText)
      ? state.strainClassSourceText
      : null;

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setFormError(null);

    const candidate = buildStrainFormCandidate(
      state,
      initial,
      forceSourceType,
    );

    const parsed = strainInputSchema.safeParse(candidate);
    if (!parsed.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = String(issue.path[0] ?? 'form');
        if (!fieldErrors[key]) fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      setFormError('Please fix the highlighted fields.');
      return;
    }

    setErrors({});
    try {
      await onSubmit(parsed.data);
    } catch (err) {
      setFormError(
        err instanceof Error ? err.message : 'Could not save the product.',
      );
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate className="stack">
      {formError && (
        <div className="error-text" role="alert">
          {formError}
        </div>
      )}

      <div className="field product-form__full-row">
        <label htmlFor="brand">Brand</label>
        <input
          id="brand"
          value={state.brand}
          onChange={(e) => set('brand', e.target.value)}
          autoComplete="off"
          maxLength={200}
        />
      </div>

      <div className="field product-form__full-row">
        <label htmlFor="productName">Product Name</label>
        <input
          id="productName"
          value={state.productName}
          onChange={(e) => set('productName', e.target.value)}
          autoComplete="off"
          maxLength={200}
        />
        {errors.name && <span className="error-text">{errors.name}</span>}
      </div>

      <div className="field product-form__full-row">
        <label htmlFor="category">Category</label>
        <select
          id="category"
          value={state.category}
          onChange={(e) => set('category', e.target.value)}
        >
          <option value="" disabled hidden>
            Select Category
          </option>
          {currentLegacyCategory ? (
            <option value={currentLegacyCategory}>
              {currentLegacyCategory} — Current Value
            </option>
          ) : null}
          {ACTIVE_PRODUCT_CATEGORIES.map((category) => (
            <option key={category} value={category}>
              {category}
            </option>
          ))}
        </select>
      </div>

      <div className="field product-form__full-row">
        <label htmlFor="strainClassSourceText">Strain Classification</label>
        <select
          id="strainClassSourceText"
          value={state.strainClassSourceText}
          onChange={(e) => set('strainClassSourceText', e.target.value)}
        >
          <option value="" disabled hidden>
            Select Strain Classification
          </option>
          {currentLegacyStrainClassification ? (
            <option value={currentLegacyStrainClassification}>
              {currentLegacyStrainClassification} — Current Value
            </option>
          ) : null}
          {ACTIVE_STRAIN_CLASSIFICATIONS.map((classification) => (
            <option key={classification} value={classification}>
              {classification}
            </option>
          ))}
        </select>
      </div>

      <section
        className="stack product-form__section"
        aria-labelledby="cannabinoids-heading"
      >
        <h2 id="cannabinoids-heading" className="product-form__section-title">
          Cannabinoids
        </h2>
        <div className="grid grid-2">
          <div className="field">
            <label htmlFor="thcPercent">THC %</label>
            <input
              id="thcPercent"
              inputMode="decimal"
              value={state.thcPercent}
              onChange={(e) => set('thcPercent', e.target.value)}
              placeholder="e.g. 26.22"
              required
            />
            {errors.thcPercent && (
              <span className="error-text">{errors.thcPercent}</span>
            )}
          </div>
          <div className="field">
            <label htmlFor="cbdPercent">CBD %</label>
            <input
              id="cbdPercent"
              inputMode="decimal"
              value={state.cbdPercent}
              onChange={(e) => set('cbdPercent', e.target.value)}
              placeholder="optional"
            />
            {errors.cbdPercent && (
              <span className="error-text">{errors.cbdPercent}</span>
            )}
          </div>
        </div>

        {OPTIONAL_PRODUCT_FIELDS.some(({ key }) =>
          visibleOptionalFields.has(key),
        ) ? (
          <div className="grid grid-2 product-form__optional-fields">
            {OPTIONAL_PRODUCT_FIELDS.filter(({ key }) =>
              visibleOptionalFields.has(key),
            ).map(({ key, label }) => (
              <div className="field product-form__optional-field" key={key}>
                <div className="row row-between product-form__optional-label">
                  <label htmlFor={key}>{label}</label>
                  <button
                    type="button"
                    className="product-form__remove-optional"
                    onClick={() => removeOptionalField(key)}
                    aria-label={`Remove ${label.replace(' %', '')}`}
                  >
                    <span aria-hidden="true">×</span>
                  </button>
                </div>
                <input
                  id={key}
                  inputMode="decimal"
                  value={state[key]}
                  onChange={(event) => set(key, event.target.value)}
                  placeholder="optional"
                />
                {errors[key] ? (
                  <span className="error-text">{errors[key]}</span>
                ) : null}
              </div>
            ))}
          </div>
        ) : null}

        <button
          type="button"
          className="btn product-form__add-optional"
          onClick={openOptionalModal}
          aria-haspopup="dialog"
          aria-expanded={optionalModalOpen}
          aria-controls="optional-cannabinoid-dialog"
          ref={addOptionalButtonRef}
        >
          + Add Cannabinoids
        </button>
      </section>

      {optionalModalOpen ? (
        <div
          className="product-form-modal"
          role="presentation"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeOptionalModal();
          }}
        >
          <div
            id="optional-cannabinoid-dialog"
            className="product-form-modal__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="optional-cannabinoid-title"
            aria-describedby="optional-cannabinoid-description"
          >
            <div className="product-form-modal__header">
              <h2 id="optional-cannabinoid-title">Add Cannabinoids</h2>
              <button
                type="button"
                className="product-form-modal__close"
                onClick={closeOptionalModal}
                aria-label="Close Add Cannabinoids"
              >
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <p id="optional-cannabinoid-description" className="muted mt-0">
              Add any additional product values. Leave fields blank if
              unavailable.
            </p>
            <div className="product-form-modal__fields">
              {OPTIONAL_PRODUCT_FIELDS.map(({ key, label }, index) => (
                <div className="field" key={key}>
                  <label htmlFor={`optional-draft-${key}`}>{label}</label>
                  <input
                    id={`optional-draft-${key}`}
                    ref={index === 0 ? firstOptionalInputRef : undefined}
                    inputMode="decimal"
                    value={optionalDraft[key]}
                    onChange={(event) => {
                      setOptionalDraft((current) => ({
                        ...current,
                        [key]: event.target.value,
                      }));
                      setOptionalDraftErrors((current) => ({
                        ...current,
                        [key]: undefined,
                      }));
                    }}
                    aria-invalid={Boolean(optionalDraftErrors[key])}
                    aria-describedby={
                      optionalDraftErrors[key]
                        ? `optional-draft-${key}-error`
                        : undefined
                    }
                    placeholder="optional"
                  />
                  {optionalDraftErrors[key] ? (
                    <span
                      id={`optional-draft-${key}-error`}
                      className="error-text"
                    >
                      {optionalDraftErrors[key]}
                    </span>
                  ) : null}
                </div>
              ))}
            </div>
            <div className="product-form-modal__actions">
              <button type="button" className="btn" onClick={closeOptionalModal}>
                Cancel
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={confirmOptionalDraft}
              >
                Add to Product
              </button>
            </div>
          </div>
        </div>
      ) : null}

      <div className="field product-form__full-row">
        <label htmlFor="lineageSourceText">Lineage</label>
        <input
          id="lineageSourceText"
          value={state.lineageSourceText}
          onChange={(e) => set('lineageSourceText', e.target.value)}
          autoComplete="off"
          maxLength={200}
        />
      </div>

      {afterLineage}

      {initial ? (
        <section
          className="stack product-form__section"
          aria-labelledby="notes-heading"
        >
          <h2 id="notes-heading" className="product-form__section-title">
            Notes
          </h2>
          <div className="field">
            <label htmlFor="notes">Personal Notes</label>
            <textarea
              id="notes"
              value={state.notes}
              onChange={(e) => set('notes', e.target.value)}
              maxLength={5000}
              placeholder="Your own session notes — never overwritten by spreadsheet pull"
            />
          </div>
        </section>
      ) : null}

      <button type="submit" className="btn btn-primary btn-lg" disabled={busy}>
        {busy ? 'Saving…' : submitLabel}
      </button>
    </form>
  );
}
